import React, { createContext, useCallback, useContext, useMemo, useState } from "react"
import usePartnerAttributes from "../hooks/usePartnerAttributes"
import useLocale from "../hooks/useLocale"
import { autofillRelatedFields, getFilteredCities, getFilteredProvinces } from "../utils/locationHelper"
import { Maps } from "../utils/locationTypes"

export interface PartnerSelectedFilters {
  city: string;
  province: string;
  country: string;
  type: string[];
  studioAmount: string;
  extra: string[];
}

export interface PartnerSearchFields {
  countrySearch: string;
  provinceSearch: string;
  citySearch: string;
}

interface ContextValues {
  selectedFilters: PartnerSelectedFilters;
  searchFields: PartnerSearchFields;
  setSelectedFilters: React.Dispatch<React.SetStateAction<PartnerSelectedFilters>>;
  setSearchFields: React.Dispatch<React.SetStateAction<PartnerSearchFields>>;
  filterOptions: {
    countries: string[];
    provinces: string[];
    cities: string[];
    types: string[];
    studioAmounts: (number | string)[];
    extras: string[];
  };
  filteredProvinces: string[];
  filteredCities: string[];
  handleFilterChange: (newFilters: Partial<PartnerSelectedFilters>) => void;
}

const PartnerFilterContext = createContext<ContextValues | null>(null)

export const PartnerFilterProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { locale } = useLocale()
  const { cityMap, provinceMap, countryMap, allTypes, allExtras } = usePartnerAttributes(locale)

  const [selectedFilters, setSelectedFilters] = useState<PartnerSelectedFilters>({
    city: "",
    country: "",
    extra: [],
    province: "",
    studioAmount: "",
    type: [],
  })

  const [searchFields, setSearchFields] = useState<PartnerSearchFields>({
    citySearch: "",
    countrySearch: "",
    provinceSearch: "",
  })

  const maps: Maps = useMemo(() => ({
    cityMap: new Map(cityMap),
    countryMap: new Map(countryMap),
    provinceMap: new Map(provinceMap),
  }), [cityMap, countryMap, provinceMap])

  const filteredProvinces = useMemo(
    () => getFilteredProvinces(maps, selectedFilters.country),
    [selectedFilters.country, maps]
  )

  const filteredCities = useMemo(
    () => getFilteredCities(maps, selectedFilters.province),
    [selectedFilters.province, maps]
  )

  const handleFilterChange = useCallback(
    (newFilters: Partial<typeof selectedFilters>) => {
      setSelectedFilters((prev) => {
        const updatedFilters = { ...prev, ...newFilters }

        // Use the utility function to autofill related fields
        const autofilledFilters = autofillRelatedFields(
          updatedFilters,
          maps
        )

        return { ...updatedFilters, ...autofilledFilters }
      })
    },
    [maps]
  )

  const filterOptions = useMemo(() => ({
    cities: Array.from(maps.cityMap.values()).map((city) => city.name),
    countries: Array.from(maps.countryMap.values()).map((country) => country.name),
    extras: allExtras.map((extra) => extra.name),
    provinces: Array.from(maps.provinceMap.values()).map((province) => province.name),
    studioAmounts: [1, 2, 3, "3+"],
    types: allTypes.map((type) => type.name),
  }), [maps, allTypes, allExtras])

  const contextValue = useMemo(() => ({
    filterOptions,
    filteredCities,
    filteredProvinces,
    handleFilterChange,
    searchFields,
    selectedFilters,
    setSearchFields,
    setSelectedFilters,
  }), [
    filterOptions,
    filteredCities,
    filteredProvinces,
    handleFilterChange,
    searchFields,
    selectedFilters,
  ])

  return (
    <PartnerFilterContext.Provider value={contextValue}>
      {children}
    </PartnerFilterContext.Provider>
  )
}

export const usePartnerFilterContext = (): ContextValues => {
  const context = useContext(PartnerFilterContext)
  if (!context)
    throw new Error("usePartnerFilterContext must be used within a PartnerFilterProvider")
  return context
}
